import api from '@api/endpoints';
import { setAlert } from '@store/actions/alertActions';
import { OUTBOUND_CALLING_OPS as types } from '@store/constants/outboundCallingConstants';
import { ALERT_STATUS_TYPE } from '@constants';
import { outboundCallingDashboardConfigBuilder } from '@views/OutboundCallingDashboard/utils';


/**
 * Get dashboard config
 */
export const getDashboardConfig = () => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_OUTBOUND_CONFIG
    });

    const data = outboundCallingDashboardConfigBuilder();

    dispatch({
      type: types.GET_OUTBOUND_CONFIG_SUCCESS,
      payload: data
    });

  } catch (error) {
    dispatch(
      setAlert({
        title: 'Failed to get Outbound Calling Config',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    dispatch({
      type: types.GET_OUTBOUND_CONFIG_FAILURE,
      payload: error?.message || 'Failed to get Outbound Calling config'
    });
  }
};


/**
 * Get agent status
 */
export const getAgentStatus =
  ({ agentId }) =>
    async (dispatch) => {
  try {
    dispatch({ type: types.GETTING_AGENT_STATUS });

    const { data } = await api.outboundCalling.getAgentStatus({ agentId });

    dispatch({
      type: types.GET_AGENT_STATUS_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    dispatch({
      type: types.GET_AGENT_STATUS_FAILURE,
      payload: error?.message || 'Failed to fetch agent status'
    });

    dispatch(
      setAlert({
        title: 'Failed to fetch agent status.',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    throw error;
  }
};


/**
 * Update agent online status
 */
export const updateAgentStatus =
  ({ agentId, isOnline, onSuccess, onComplete }) =>
  async (dispatch) => {
    try {
      dispatch({ type: types.SETTING_AGENT_STATUS });

      await api.outboundCalling.updateAgentStatus({ agentId, isOnline });

      dispatch({
        type: types.SET_AGENT_STATUS_SUCCESS,
        payload: { isOnline }
      });

      dispatch(
        setAlert({
          title: `Status updated to ${isOnline ? 'Active' : 'Inactive'}`,
          status: ALERT_STATUS_TYPE.SUCCESS
        })
      );

      onSuccess && onSuccess();
      return { isOnline };
    } catch (error) {
      dispatch({
        type: types.SET_AGENT_STATUS_FAILURE,
        payload: error?.message || 'Failed to update agent status'
      });

      dispatch(
        setAlert({
          title: 'Failed to update agent status',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );

      throw error;
    } finally {
      onComplete && onComplete();
    }
  };


/**
 * Get tickets by agent ID with optional filters
 * @param {string} agentId - Agent ID
 * @param {object} payload - Optional query parameters
 * @param {string} payload.status - Filter by ticket status
 * @param {string} payload.ticketId - Filter by ticket ID
 * @param {string} payload.customerName - Filter by customer name
 * @param {string} payload.orderId - Filter by order ID
 * @param {number} payload.page - Page number (default: 0)
 * @param {number} payload.pageSize - Page size (default: 5)
 * @param {function} onSuccess - Success callback
 * @param {function} onComplete - Complete callback
 */
export const getTicketsByAgent =
  ({ agentId, payload = {}, onSuccess, onComplete }) =>
  async (dispatch) => {
    try {
      dispatch({ type: types.GETTING_TICKETS });

      const { data } = await api.outboundCalling.getTicketsByAgent({
        agentId,
        payload
      });

      // REVIEW :: Check if data is already paginated or needs transformation
      const transformedData = data.data && data.totalResults !== undefined
        ? data // Already paginated from backend
        : {
            // Transform for backward compatibility
            data: data,
            totalResults: data.length,
            totalPages: 1,
            currentPage: payload.page || 0
          };

      dispatch({
        type: types.GET_TICKETS_SUCCESS,
        // payload: data
        payload: transformedData
      });

      onSuccess && onSuccess(transformedData);
    } catch (error) {
      dispatch({
        type: types.GET_TICKETS_FAILURE,
        payload: error?.message || 'Failed to fetch tickets'
      });

      dispatch(
        setAlert({
          title: "Failed to fetch ticket list.",
          status: ALERT_STATUS_TYPE.ERROR,
        })
      );

      throw error;
    } finally {
      onComplete && onComplete();
    }
  };

/**
 * Update ticket (status, priority, etc.)
 */
export const updateTicket =
  ({ ticketId, agentId, updates, onSuccess, onComplete }) =>
  async (dispatch) => {
    try {
      dispatch({ type: types.UPDATING_TICKET });

      const { data } = await api.outboundCalling.updateTicket({
        ticketId,
        agentId,
        updates
      });

      dispatch({
        type: types.UPDATE_TICKET_SUCCESS,
        payload: data
      });

      dispatch(
        setAlert({
          title: 'Ticket updated successfully',
          status: ALERT_STATUS_TYPE.SUCCESS
        })
      );

      onSuccess && onSuccess(data);
      return data;
    } catch (error) {
      dispatch({
        type: types.UPDATE_TICKET_FAILURE,
        payload: error?.message || 'Failed to update ticket'
      });

      dispatch(
        setAlert({
          title: "Failed to update the ticket.",
          status: ALERT_STATUS_TYPE.ERROR,
        })
      );


      throw error;
    } finally {
      onComplete && onComplete();
    }
  };


/**
 * Get admin dashboard data
 * Fetches agent availability, queue details, and call analytics
 */
export const fetchOverallOutboundCallingStats = () => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_ADMIN_DASHBOARD_STATS
    });

    const { data } = await api.outboundCalling.getAdminDashboardData();

    dispatch({
      type: types.GET_ADMIN_DASHBOARD_STATS_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    dispatch({
      type: types.GET_ADMIN_DASHBOARD_STATS_FAILURE,
      payload: error?.message || 'Failed to fetch admin dashboard data'
    });

    dispatch(
      setAlert({
        title: "Failed to get the stats.",
        status: ALERT_STATUS_TYPE.ERROR,
      })
    );

    throw error;
  }
};

/**
 * Set current ticket for modal
 */
export const setCurrentTicket = (ticket) => ({
  type: types.SET_CURRENT_TICKET,
  payload: ticket
});

/**
 * Clear current ticket
 */
export const clearCurrentTicket = () => ({
  type: types.CLEAR_CURRENT_TICKET
});

/**
 * Get available agents list (Admin Dashboard)
 */
export const fetchAvailableAgents = () => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_AVAILABLE_AGENTS
    });

    const { data } = await api.outboundCalling.getAvailableAgents();

    dispatch({
      type: types.GET_AVAILABLE_AGENTS_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    dispatch({
      type: types.GET_AVAILABLE_AGENTS_FAILURE,
      payload: error?.message || 'Failed to fetch available agents'
    });

    dispatch(
      setAlert({
        title: 'Failed to fetch available agents',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    throw error;
  }
};

/**
 * Get tickets queue - open tickets (Admin Dashboard)
 * @param {object} payload - Query parameters
 */
export const fetchTicketsQueue = (payload = {}) => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_TICKETS_QUEUE
    });

    const { data } = await api.outboundCalling.getTicketsQueue(payload);

    dispatch({
      type: types.GET_TICKETS_QUEUE_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    dispatch({
      type: types.GET_TICKETS_QUEUE_FAILURE,
      payload: error?.message || 'Failed to fetch tickets queue'
    });

    dispatch(
      setAlert({
        title: 'Failed to fetch tickets queue',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    throw error;
  }
};

/**
 * Get closed tickets (Admin Dashboard)
 * @param {object} payload - Query parameters including lastHours
 */
export const fetchClosedTickets = (payload = {}) => async (dispatch) => {
  try {
    dispatch({
      type: types.GETTING_CLOSED_TICKETS
    });

    const { data } = await api.outboundCalling.getClosedTickets(payload);

    dispatch({
      type: types.GET_CLOSED_TICKETS_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    dispatch({
      type: types.GET_CLOSED_TICKETS_FAILURE,
      payload: error?.message || 'Failed to fetch closed tickets'
    });

    dispatch(
      setAlert({
        title: 'Failed to fetch closed tickets',
        status: ALERT_STATUS_TYPE.ERROR
      })
    );

    throw error;
  }
};